import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApplicationHttpClient } from 'app/components/shared/http/application-http-client';
import { Routes } from '../../config/routes';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { JwtLegFiClaims } from '../auth/jwt-legfi-claims.model';

/** Status options on the lesson row. Mirrors `gable_lessons.status`. */
export type GableLessonStatus = 'active' | 'draft' | 'retired';

/** Lesson row shape returned from the API. */
export interface GableLesson {
    id: number;
    title: string;
    triggerContext: string;
    guidance: string;
    tags: string[] | null;
    /**
     * Read-only vision summaries of screenshots the author uploaded at
     * lesson-creation time. The raw images are NOT stored — backend
     * passes them through Claude vision once on save and persists only
     * the resulting short descriptions.
     */
    screenshotSummaries: string[] | null;
    status: GableLessonStatus;
    fireCount: number;
    lastFiredAt: string | null;
    createdAt: string | null;
    updatedAt: string | null;
    /**
     * Embed-pipeline status, ONLY present on the response from a mutation
     * (store / update / destroy). `'ok'` means the pgvector row landed;
     * `'pending'` means the embed call threw and was swallowed server-side
     * — the MariaDB row is canonical but the vector needs a manual reindex
     * before Gable can retrieve the lesson at chat time. Read endpoints
     * (index, similar) omit this field entirely.
     */
    embedStatus?: 'ok' | 'pending';
}

/** Body for POST/PATCH. PATCH fields are all optional. */
export interface GableLessonInput {
    title?: string;
    triggerContext?: string;
    guidance?: string;
    tags?: string[] | null;
    /**
     * Transient: pass base64 data URLs for any newly-uploaded screenshots.
     * Backend vision-summarizes them and persists only the resulting text
     * descriptions on `screenshotSummaries`; the raw bytes are dropped.
     */
    screenshotDataUrls?: string[] | null;
    status?: GableLessonStatus;
    /**
     * Optional: when this lesson is being authored from a feedback
     * row on the AI Activity page, pass the event id. The backend
     * stamps `ai_interaction_events.promoted_lesson_id` in the same
     * transaction as the lesson insert so the same exchange can't
     * be promoted into duplicate lessons. Honored on store() only.
     */
    fromInteractionEventId?: number;
}

/**
 * Thin client for the Gable lessons admin API. The Teach Gable dialog
 * and the lessons management page both consume this. Super-only routes
 * — the backend gate refuses non-super even if these helpers got called.
 */
@Injectable({ providedIn: 'root' })
export class GableLessonsService {
    constructor(private readonly _http: ApplicationHttpClient) {}

    list(params?: { status?: GableLessonStatus; q?: string }): Observable<{ lessons: GableLesson[] }> {
        const jwt = LegFiJwtService.read();
        if (jwt === null) {
            throw new Error('No JWT — cannot list lessons');
        }

        let query = '';
        if (params?.status || params?.q) {
            const parts: string[] = [];
            if (params.status) parts.push(`status=${encodeURIComponent(params.status)}`);
            if (params.q) parts.push(`q=${encodeURIComponent(params.q)}`);
            query = '?' + parts.join('&');
        }

        return this._http.get<{ lessons: GableLesson[] }>(this.url(jwt) + query);
    }

    create(body: GableLessonInput): Observable<{ lesson: GableLesson }> {
        const jwt = this.requireJwt();
        return this._http.post<{ lesson: GableLesson }>(
            this.url(jwt),
            this._http.serializeObject(body as { [k: string]: any }),
        );
    }

    update(id: number, body: GableLessonInput): Observable<{ lesson: GableLesson }> {
        const jwt = this.requireJwt();
        return this._http.patch<{ lesson: GableLesson }>(
            this.itemUrl(jwt, id),
            this._http.serializeObject(body as { [k: string]: any }),
        );
    }

    /** Soft delete — flips status to retired. */
    retire(id: number): Observable<{ lesson: GableLesson }> {
        const jwt = this.requireJwt();
        return this._http.delete<{ lesson: GableLesson }>(this.itemUrl(jwt, id));
    }

    /**
     * Authoring-time dedup helper. Returns the top-3 active lessons whose
     * trigger embeddings are similar to the in-progress trigger text.
     */
    similar(triggerContext: string): Observable<{ matches: Array<GableLesson & { similarity: number }> }> {
        const jwt = this.requireJwt();
        const url = Routes.MakeLegFiCoreUrl(Routes.AssistantPocModule.LessonsSimilar(jwt.orgId));
        return this._http.post<{ matches: Array<GableLesson & { similarity: number }> }>(
            url,
            this._http.serializeObject({ triggerContext } as { [k: string]: any }),
        );
    }

    private url(jwt: JwtLegFiClaims): string {
        return Routes.MakeLegFiCoreUrl(Routes.AssistantPocModule.Lessons(jwt.orgId));
    }

    private itemUrl(jwt: JwtLegFiClaims, id: number): string {
        return Routes.MakeLegFiCoreUrl(Routes.AssistantPocModule.Lesson(jwt.orgId, id));
    }

    private requireJwt(): JwtLegFiClaims {
        const jwt = LegFiJwtService.read();
        if (jwt === null) {
            throw new Error('No JWT — cannot call lessons endpoint');
        }
        return jwt;
    }
}
